import { FiBookOpen } from 'react-icons/fi';

export const academicCollocationsData = {
  courseId: 'academic-collocations',
  courseType: 'vocabulary',
  title: 'Academic Collocations',
  icon: FiBookOpen,
  overview: {
    impact: 'Sound natural and authoritative with correct word pairings',
    focus: 'Verb + noun and adjective + noun collocations used in academic English',
    uses: 'Writing Tasks 1 & 2, Speaking Part 3, Reading comprehension',
    duration: '20-25 minutes',
  },
  sections: [
    {
      title: 'What Are Academic Collocations?',
      content: `<p>A <strong>collocation</strong> is a pair or group of words that naturally go together in English. Native speakers say "conduct research" — never "do a research" or "make research." Using the correct collocation makes your English sound fluent and natural; using the wrong one immediately signals a non-native speaker.</p>
<p>In IELTS, collocations are especially important for Writing because examiners assess <em>collocation awareness</em> as part of the Lexical Resource criterion. A candidate who writes "pose a threat" instead of "give a threat" demonstrates higher language proficiency, even if the grammar and meaning are identical.</p>`,
      points: [
        'Collocations are fixed word combinations that native speakers use naturally',
        'Wrong collocations (e.g., "do a research") sound unnatural even if grammatically possible',
        'IELTS Lexical Resource explicitly rewards accurate collocation use',
        'Academic collocations differ from everyday collocations — "draw conclusions" vs "draw a picture"',
      ],
    },
    {
      title: 'Verb + Noun Collocations',
      content: `<p>These verb + noun pairs are among the most useful for IELTS academic writing. Learn them as fixed units.</p>
<table>
  <thead><tr><th>Collocation</th><th>Meaning</th><th>Common Mistake</th><th>Example</th></tr></thead>
  <tbody>
    <tr><td><strong>conduct research</strong></td><td>carry out a systematic investigation</td><td>❌ do a research</td><td>Scientists conducted research into the effects of pollution.</td></tr>
    <tr><td><strong>draw conclusions</strong></td><td>form a judgement based on evidence</td><td>❌ make conclusions</td><td>It is difficult to draw conclusions from such limited data.</td></tr>
    <tr><td><strong>raise awareness</strong></td><td>make people more informed about an issue</td><td>❌ rise awareness</td><td>Campaigns can raise awareness of mental health issues.</td></tr>
    <tr><td><strong>pose a threat</strong></td><td>represent a danger</td><td>❌ give a threat</td><td>Climate change poses a serious threat to biodiversity.</td></tr>
    <tr><td><strong>gain insight</strong></td><td>develop understanding</td><td>❌ get insight</td><td>The study helped us gain insight into consumer behaviour.</td></tr>
    <tr><td><strong>take into account</strong></td><td>consider as a factor</td><td>❌ take in account</td><td>Policymakers must take cultural differences into account.</td></tr>
    <tr><td><strong>exert influence</strong></td><td>use power to affect something</td><td>❌ make influence</td><td>The media exerts considerable influence on public opinion.</td></tr>
    <tr><td><strong>yield results</strong></td><td>produce outcomes</td><td>❌ give results</td><td>The new teaching method has yielded promising results.</td></tr>
    <tr><td><strong>address an issue</strong></td><td>deal with or tackle a problem</td><td>❌ solve an issue (less academic)</td><td>The report addresses several issues related to housing policy.</td></tr>
    <tr><td><strong>bridge the gap</strong></td><td>reduce the difference between two things</td><td>❌ close the gap (less formal)</td><td>Education can help bridge the gap between rich and poor.</td></tr>
  </tbody>
</table>`,
      points: [
        'Learn collocations as fixed pairs — never separate and recombine the parts',
        'The "common mistake" column shows errors that IELTS candidates frequently make',
        '"Conduct research" and "draw conclusions" are the two most common academic collocations',
        '"Address an issue" sounds more academic than "solve a problem" in Task 2',
      ],
    },
    {
      title: 'Adjective + Noun Collocations',
      content: `<p>Adjective + noun collocations add precision and formality to your writing. Instead of "a big effect," write "a significant impact" — it means the same thing but sounds far more academic.</p>
<table>
  <thead><tr><th>Collocation</th><th>Common Alternative</th><th>Example</th></tr></thead>
  <tbody>
    <tr><td><strong>significant impact</strong></td><td>big effect</td><td>Technology has had a significant impact on healthcare.</td></tr>
    <tr><td><strong>growing concern</strong></td><td>increasing worry</td><td>Air quality is a growing concern in major cities.</td></tr>
    <tr><td><strong>key factor</strong></td><td>important thing</td><td>Education is a key factor in reducing poverty.</td></tr>
    <tr><td><strong>fundamental principle</strong></td><td>basic rule</td><td>Equality is a fundamental principle of democratic societies.</td></tr>
    <tr><td><strong>compelling evidence</strong></td><td>strong proof</td><td>There is compelling evidence that early education improves outcomes.</td></tr>
    <tr><td><strong>stark contrast</strong></td><td>big difference</td><td>There is a stark contrast between urban and rural healthcare.</td></tr>
    <tr><td><strong>viable alternative</strong></td><td>possible option</td><td>Public transport is a viable alternative to private car use.</td></tr>
    <tr><td><strong>profound effect</strong></td><td>deep impact</td><td>The industrial revolution had a profound effect on society.</td></tr>
  </tbody>
</table>`,
      points: [
        'Adjective + noun collocations are the easiest way to upgrade your writing instantly',
        '"Significant impact" and "key factor" are versatile enough for almost any essay topic',
        '"Growing concern" is perfect for problem-solution essays and environmental topics',
        'Use "compelling evidence" when introducing strong supporting data or research',
      ],
    },
    {
      title: 'Using Them in IELTS Tasks',
      content: `<p>The best strategy is to memorise 10-12 collocations and deliberately practise using them in timed essay writing. Over time, they will become automatic.</p>
<h4>Task 2 Body Paragraph Example</h4>
<p><em>"Air pollution poses a serious threat to public health, and it is a growing concern in rapidly developing nations. While governments have conducted research into the causes, few have taken practical steps to address the issue. Compelling evidence suggests that investing in renewable energy could yield significant results."</em></p>
<p>This paragraph uses five academic collocations naturally — "poses a threat," "growing concern," "conducted research," "address the issue," and "compelling evidence" — without sounding forced.</p>
<h4>Task 1 Data Commentary Example</h4>
<p><em>"The data reveals a stark contrast between spending on education in developed and developing countries, with the gap narrowing only slightly over the period."</em></p>`,
      points: [
        'Aim for 4-6 collocations per Task 2 essay, spread across body paragraphs',
        'In Task 1, use "significant increase/decrease", "stark contrast", "key factor"',
        'In Speaking Part 3, use them when discussing societal issues and abstract topics',
        'Write practice essays and circle every collocation — if fewer than 4, revise',
      ],
    },
  ],
  exercises: [
    {
      title: 'Match the Collocations',
      type: 'select',
      instructions: 'Select the correct verb or adjective to complete the academic collocation.',
      questions: [
        {
          text: '_______ research (carry out a study)',
          options: ['make', 'conduct', 'do', 'create'],
          answer: 'conduct',
          explanation: 'The correct academic collocation is "conduct research." While "do research" is acceptable in informal contexts, "conduct research" is the academic standard.',
        },
        {
          text: '_______ conclusions (form a judgement)',
          options: ['make', 'take', 'draw', 'get'],
          answer: 'draw',
          explanation: 'The correct collocation is "draw conclusions." We draw conclusions from evidence, data, or analysis.',
        },
        {
          text: '_______ a threat (represent a danger)',
          options: ['give', 'put', 'pose', 'make'],
          answer: 'pose',
          explanation: 'The correct collocation is "pose a threat." This is a fixed academic combination meaning to represent a danger.',
        },
        {
          text: '_______ impact (a large effect)',
          options: ['big', 'significant', 'large', 'high'],
          answer: 'significant',
          explanation: 'The correct academic collocation is "significant impact." While "big impact" is understood, "significant impact" is the academic standard.',
        },
      ],
    },
    {
      title: 'Complete with the Correct Collocation',
      type: 'fill-in',
      instructions: 'Fill in the blank with the correct academic collocation from the lesson.',
      questions: [
        {
          text: 'The charity launched a campaign to _______ of the dangers of smoking among teenagers.',
          answer: 'raise awareness',
          explanation: '"Raise awareness" is the correct collocation meaning to make people more informed about an issue.',
        },
        {
          text: 'When making policy decisions, governments must _______ the needs of all citizens _______.',
          answer: 'take into account',
          explanation: '"Take into account" means to consider as a relevant factor when making decisions.',
        },
        {
          text: 'There is a _______ between the quality of education in wealthy and disadvantaged areas.',
          answer: 'stark contrast',
          explanation: '"Stark contrast" is the academic collocation describing a very obvious and striking difference.',
        },
        {
          text: 'The new curriculum has _______ in terms of student engagement and exam performance.',
          answer: 'yielded results',
          explanation: '"Yielded results" is the academic way of saying "produced outcomes" — it collocates naturally with research and programmes.',
        },
      ],
    },
    {
      title: 'Fix the Wrong Collocations',
      type: 'select',
      instructions: 'Identify the incorrect collocation in each sentence.',
      questions: [
        {
          text: '"Scientists have made extensive research into the effects of microplastics on marine life."',
          options: ['made extensive research', 'effects of microplastics', 'on marine life', 'No error'],
          answer: 'made extensive research',
          explanation: 'The correct collocation is "conducted extensive research." "Made research" is not a standard English collocation.',
        },
        {
          text: '"The report helped policymakers to get insight into the root causes of youth unemployment."',
          options: ['helped policymakers', 'get insight', 'root causes', 'youth unemployment'],
          answer: 'get insight',
          explanation: 'The correct collocation is "gain insight." "Get insight" is informal; "gain" is the academic verb partner.',
        },
        {
          text: '"Rapid urbanisation gives a serious threat to the natural environment in developing countries."',
          options: ['Rapid urbanisation', 'gives a serious threat', 'natural environment', 'developing countries'],
          answer: 'gives a serious threat',
          explanation: 'The correct collocation is "poses a serious threat." "Gives a threat" is not a natural English collocation.',
        },
        {
          text: '"Education is a basic principle of social equality and human development."',
          options: ['Education is', 'a basic principle', 'social equality', 'human development'],
          answer: 'a basic principle',
          explanation: 'While not strictly wrong, the academic collocation is "fundamental principle." "Basic principle" is less formal and less precise.',
        },
      ],
    },
    {
      title: 'Choose the Academic Collocation',
      type: 'select',
      instructions: 'Select the option that uses the most academic collocation.',
      questions: [
        {
          text: 'Which is most academic?',
          options: [
            'Technology has had a big effect on society.',
            'Technology has had a significant impact on society.',
            'Technology has had a large result on society.',
          ],
          answer: 'Technology has had a significant impact on society.',
          explanation: '"Significant impact" is the standard academic collocation. "Big effect" is too informal, and "large result" is not a natural collocation.',
        },
        {
          text: 'Which is most academic?',
          options: [
            'The data makes it possible to get conclusions about the trend.',
            'The data allows us to draw conclusions about the trend.',
            'The data lets us make conclusions about the trend.',
          ],
          answer: 'The data allows us to draw conclusions about the trend.',
          explanation: '"Draw conclusions" is the correct academic collocation. "Get conclusions" and "make conclusions" are non-standard.',
        },
        {
          text: 'Which is most academic?',
          options: [
            'Pollution is a growing concern in urban areas.',
            'Pollution is an increasing worry in urban areas.',
            'Pollution is a bigger problem in urban areas.',
          ],
          answer: 'Pollution is a growing concern in urban areas.',
          explanation: '"Growing concern" is the established academic collocation. "Increasing worry" is less formal and "bigger problem" is informal.',
        },
      ],
    },
    {
      title: 'Write with Academic Collocations',
      type: 'writing',
      instructions: 'Write a Task 2-style sentence that naturally includes the given academic collocation.',
      questions: [
        {
          text: 'Write a sentence using "bridge the gap".',
          sampleAnswer: 'Investment in public education is essential to bridge the gap between socioeconomic groups and ensure equal opportunities for all citizens.',
          explanation: '"Bridge the gap" is used correctly to describe reducing a difference between two groups.',
        },
        {
          text: 'Write a sentence using "compelling evidence" and "address the issue".',
          sampleAnswer: 'There is compelling evidence that rising sea levels threaten coastal communities, and governments must act urgently to address the issue.',
          explanation: 'Both collocations are used naturally — "compelling evidence" introduces the problem and "address the issue" calls for action.',
        },
      ],
    },
  ],
  tips: [
    'Learn collocations as fixed pairs — write them together in your vocabulary notebook',
    'The biggest collocation errors: "make research" (→ conduct), "do conclusion" (→ draw), "give threat" (→ pose)',
    'Read IELTS model essays and underline every collocation pair you find',
    'In Task 1, "significant increase/decrease" and "stark contrast" are especially useful',
    'Test yourself: cover the verb column and try to recall it from the noun alone',
  ],
  dailyPractice: [
    'Write three Task 2 sentences, each containing at least one academic collocation',
    'Read a newspaper article and identify verb + noun collocations you could use in IELTS',
    'Create flashcards: noun on front, correct verb partner on back — test yourself daily',
    'Review a past IELTS essay you wrote and upgrade at least 3 word combinations to academic collocations',
  ],
  backPath: '/exams/english/#vocabulary',
  backLabel: 'Back to Vocabulary',
  aiContext: 'Academic Collocations for IELTS: Verb + noun pairs (conduct research, draw conclusions, raise awareness, pose a threat, gain insight, take into account) and adjective + noun pairs (significant impact, growing concern, key factor, fundamental principle, compelling evidence, stark contrast). Difficulty 4.',
};

export function getDataForDifficulty(difficulty = 4) {
  if (difficulty <= 2) {
    return {
      ...academicCollocationsData,
      title: academicCollocationsData.title + ' (Foundations)',
      overview: { ...academicCollocationsData.overview, duration: '15-20 minutes' },
      sections: academicCollocationsData.sections.slice(0, Math.max(2, Math.ceil(academicCollocationsData.sections.length / 2))),
      exercises: academicCollocationsData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...academicCollocationsData,
      title: academicCollocationsData.title + ' (Advanced)',
      overview: { ...academicCollocationsData.overview, duration: '30-35 minutes' },
      sections: academicCollocationsData.sections,
      exercises: academicCollocationsData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(academicCollocationsData.tips || []),
        'For Band 8+: Use less common vocabulary naturally in context.',
        'Advanced tip: Focus on collocations and word families, not just individual words.',
      ],
    };
  }
  return academicCollocationsData;
}
